// @flow

import {
  curryRightN,
  cond,
  constant,
  flow,
  reject,
  filter,
  flatMap,
  gt,
  gte,
  identity,
  includes,
  isNil,
  lte,
  matches,
  placeholder,
  stubTrue,
  where
} from 'lodash/fp';

import { FEATURED, FREE, PAID, H2H } from '../constants/challengeCategoryTypes';
import { LINE_UP_SMALL, LINE_UP_MEDIUM, LINE_UP_LARGE } from '../constants/lineUpSizeTypes';
import {
  COMPLEXITY,
  MAX_ENTRY_CAP,
  MAX_FEE,
  MIN_ENTRY_CAP,
  MIN_FEE,
  PAYOUT,
  PLAYERS
} from '../constants/filterTypes';
import { MULTIPLE, SINGLE } from '../constants/complexityTypes';
import * as challengeSubTypes from '../constants/challengeSubTypes';
import { ALL_GUARANTEED } from '../constants/payoutTypes';

// more convenient forms
const greaterThan = curryRightN(2, gt);
const greaterThanOrEqual = curryRightN(2, gte);
const lessThanOrEqual = curryRightN(2, lte);
const isOneOf = curryRightN(2, includes);

function filterByCategory(category) {
  switch (category) {
    case FEATURED:
      return filter(ch => ch.isFeatured || ch.isRapidfire);
    case FREE:
      return filter({ cost: 0 });
    case PAID:
      return filter(where({ cost: greaterThan(0) }));
    case H2H:
      return filter({ subTypeId: challengeSubTypes.H2H });
    default:
  }

  return identity;
}

function rejectHeadToHeads() {
  return reject({ subTypeId: challengeSubTypes.H2H });
}

function filterByComplexity(complexities = []) {
  if (complexities.length === 0) {
    return identity;
  }

  const hasComplexity = flow(
    isOneOf(complexities),
    constant
  );

  return filter(
    cond([
      [matches({ gameCount: 1 }), hasComplexity(SINGLE)],
      [where({ gameCount: greaterThanOrEqual(2) }), hasComplexity(MULTIPLE)]
    ])
  );
}

function filterByCostMax(value) {
  return isNil(value) ? identity : filter(where({ cost: lessThanOrEqual(value) }));
}

function filterByCostMin(value) {
  return isNil(value) ? identity : filter(where({ cost: greaterThanOrEqual(value) }));
}

function filterByEntryCapMax(value) {
  return isNil(value) ? identity : filter(where({ entryCap: lessThanOrEqual(value) }));
}

function filterByEntryCapMin(value) {
  return isNil(value) ? identity : filter(where({ entryCap: greaterThanOrEqual(value) }));
}

function filterByLineUpSize(sizes = []) {
  if (sizes.length === 0) {
    return identity;
  }

  const hasSize = flow(
    isOneOf(sizes),
    constant
  );

  return filter(
    cond([
      [where({ playerQuota: lessThanOrEqual(5) }), hasSize(LINE_UP_SMALL)],
      [where({ playerQuota: lessThanOrEqual(10) }), hasSize(LINE_UP_MEDIUM)],
      [stubTrue, hasSize(LINE_UP_LARGE)]
    ])
  );
}

function filterByPayout(payouts = []) {
  if (payouts.length === 0) {
    return identity;
  }
  const mappedPayouts = flatMap(p => (p === 'guaranteed' ? ALL_GUARANTEED : p), payouts);

  return filter(
    where({
      payoutType: includes(placeholder, mappedPayouts)
    })
  );
}

function challengeFilter(opts = {}) {
  return flow(
    filterByCategory(opts.category),
    opts.category !== H2H ? rejectHeadToHeads() : identity,
    filterByComplexity(opts[COMPLEXITY]),
    filterByLineUpSize(opts[PLAYERS]),
    filterByCostMax(opts[MAX_FEE]),
    filterByCostMin(opts[MIN_FEE]),
    filterByEntryCapMax(opts[MAX_ENTRY_CAP]),
    filterByEntryCapMin(opts[MIN_ENTRY_CAP]),
    filterByPayout(opts[PAYOUT])
  );
}

export default challengeFilter;
